import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import styled from 'styled-components';
import { t } from 'ttag';
import useBasketContext from '../../../../../contexts/basket/useBasketContext';
import useGroupContext from '../../../../../contexts/group/useGroupContext';
import useSiteContext from '../../../../../contexts/site/useSiteContext';
import { CTA } from '../../../../general/Button';
import TinyModal from '../../../../general/TinyModal';
import CopyLinkButton from './CopyLinkButton';
import GroupNameForm from './GroupNameForm';
import useShareLink from './useShareLink';

type GroupStartModalProps = {
    show: boolean;
    onHide: () => void;
};

// Starting a group order: who you are, and then the link to share with guests
const GroupStartModal = ({ show, onHide }: GroupStartModalProps) => {
    const groupContext = useGroupContext();
    const basketContext = useBasketContext();
    const siteContext = useSiteContext();
    const link = useShareLink();

    const [creating, setCreating] = useState(false);

    const create = async (name: string) => {
        if (!siteContext.site) return;

        setCreating(true);

        try {
            await groupContext.start(siteContext.site.uid, name, basketContext.basketItems);
        } catch (error) {
            // (the API said why)
        } finally {
            setCreating(false);
        }
    };

    // once it's started there's a link to share
    if (groupContext.active && groupContext.session?.isLead) {
        return (
            <TinyModal
                show={show}
                onHide={onHide}
                modalID="group-start-modal"
                header={t`Your group order`}
            >
                <>
                    <p>{t`Share the link with your guests so they can add their own items. When everyone has finished, choose a time and check out.`}</p>
                    <Form.Group controlId="group-share-link" className="mb-0">
                        <Form.Label>{t`Share this link with guests`}</Form.Label>
                        <LinkRow>
                            <Form.Control type="text" readOnly value={link} onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()} />
                            <CopyLinkButton link={link} secondary />
                        </LinkRow>
                    </Form.Group>
                    <Buttons>
                        <CTA type="button" block onClick={onHide}>
                            <span>{t`Back to menu`}</span>
                        </CTA>
                    </Buttons>
                </>
            </TinyModal>
        );
    }

    return (
        <TinyModal
            show={show}
            onHide={onHide}
            modalID="group-start-modal"
            header={t`Start a group order`}
        >
            <>
                <p>{t`Share a link with your guests so they can add their own items to one order. You choose the time, check out and pay.`}</p>
                <GroupNameForm id="group-start-name" onSubmit={create} busy={creating} label={t`What is your name?`} help={t`Guests see this as the name of the person hosting`}>
                    {(submit) => (
                        <>
                            <CTA type="button" secondary buddies onClick={onHide}>
                                <span>{t`Cancel`}</span>
                            </CTA>
                            <CTA type="button" buddies loading={creating} onClick={submit}>
                                <span>{t`Continue`}</span>
                            </CTA>
                        </>
                    )}
                </GroupNameForm>
            </>
        </TinyModal>
    );
};

const LinkRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: stretch;

    input {
        flex: 1;
        min-width: 0;
    }

    button {
        width: auto;
        flex-shrink: 0;
    }
`;

const Buttons = styled.div`
    display: flex;
    gap: 16px;
    margin-top: 24px;
`;

export default GroupStartModal;
